import React, {useCallback, useMemo} from 'react';
import {View, TouchableOpacity, StyleSheet} from 'react-native';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import AVText from 'src/components/elements/AVText';
import {
  ShortCutItem,
  ShortcutType,
} from 'src/components/elements/home/ShortcutBar';
import DinningShortcut from 'src/components/img/svg/shortcuts/DinningShortcut';
import PayShortcut from 'src/components/img/svg/shortcuts/PayShortcut';
import ScanProductShortcut from 'src/components/img/svg/shortcuts/ScanProductShortcut';
import VendingShortcut from 'src/components/img/svg/shortcuts/VendingShortcut';
import WalletShortcut from 'src/components/img/svg/shortcuts/WalletShortcut';
import {getDescriber} from 'src/components/screens/home/descriptor/DescriptorType';
import Styles from 'src/components/Styles';
import BuildTypeConstants from 'src/constants/BuildTypeConstants';
import {BuildTypeEnum} from 'src/constants/BuildTypeEnum';
import Settings from 'src/Settings';
import {CampusLocationType} from 'src/types/CampusLocationTypes';

export const shortcutIconColors = () => {
  switch (Settings.buildType) {
    case BuildTypeConstants.canteen:
      return {
        primaryColor: Styles.Colors.Canteen.green,
        secondaryColor: Styles.Colors.Canteen.lightGreen,
        strokeColor: Styles.Colors.PayNew.neutralHuesBase12,
        tertiaryColor: Styles.Colors.Canteen.green,
      };
    case BuildTypeConstants.refive:
      return {
        primaryColor: Styles.Colors.Refive.notificationBorderColor,
        secondaryColor: Styles.Colors.Refive.green,
        strokeColor: Styles.Colors.PayNew.blueHuesBase08,
        tertiaryColor: Styles.Colors.PayNew.blueHuesLight,
      };
    case BuildTypeConstants.companykitchen:
      return {
        primaryColor: Styles.Colors.CompanyKitchen.primary,
        secondaryColor: Styles.Colors.CompanyKitchen.green,
        strokeColor: Styles.Colors.PayNew.blueHuesBase07,
        tertiaryColor: Styles.Colors.CompanyKitchen.primary,
      };
    case BuildTypeEnum.aramark:
      return {
        primaryColor: Styles.Colors.Aramark.primary,
        secondaryColor: Styles.Colors.Aramark.carib,
        strokeColor: Styles.Colors.PayNew.neutralHuesBase12,
        tertiaryColor: Styles.Colors.Aramark.primary,
      };
    default:
      return {
        primaryColor: Styles.Colors.PayNew.blueHuesDark,
        secondaryColor: Styles.Colors.PayNew.accent02,
        strokeColor: Styles.Colors.PayNew.blueHuesBase07,
        tertiaryColor: Styles.Colors.PayNew.blueHuesLight,
      };
  }
};

type ShortcutButtonProps = {
  shortcut: ShortCutItem;
  onDiningClick?: (
    machine: CampusLocationType,
    autoAdvanced?: boolean,
  ) => Promise<void>;
  onScanAndPayClick?: (locations: CampusLocationType[]) => void;
  onVendingClick?: (locations: CampusLocationType[]) => void;
};

const ShortcutButton = (props: ShortcutButtonProps) => {
  const {shortcut} = props;

  const hasLocations =
    Array.isArray(shortcut?.locations) && shortcut?.locations?.length > 0;

  const isDisabled =
    (shortcut.type === ShortcutType.Dining &&
      (!hasLocations || !props.onDiningClick)) ||
    (shortcut.type === ShortcutType.Vending &&
      (!hasLocations || !props.onVendingClick)) ||
    (shortcut.type === ShortcutType.ScanProduct &&
      (!hasLocations || !props.onScanAndPayClick));

  const icon = useMemo(() => {
    switch (shortcut.type) {
      case ShortcutType.Dining:
        return <DinningShortcut />;
      case ShortcutType.Vending:
        return <VendingShortcut />;
      case ShortcutType.ScanProduct:
        return <ScanProductShortcut />;
      case ShortcutType.Wallet:
        return <WalletShortcut />;
      case ShortcutType.Pay:
        return <PayShortcut />;
    }
  }, []);

  const onClick = useCallback(() => {
    switch (shortcut.type) {
      case ShortcutType.Dining:
        if (!isDisabled) props?.onDiningClick(shortcut.locations[0]);
        return;
      case ShortcutType.Vending:
        if (!isDisabled) props?.onVendingClick(shortcut.locations);
        return;
      case ShortcutType.ScanProduct:
        if (!isDisabled) props?.onScanAndPayClick(shortcut.locations);
        return;
      case ShortcutType.Wallet:
        NavActions.nestedPush([AppRoutes.Account], AppRoutes.PDCards, {
          hideBackArrow: false,
        });
        return;
      case ShortcutType.Pay:
        NavActions.navigate(AppRoutes.PayTab);
        return;
    }
  }, [props.shortcut?.locations]);

  return (
    <TouchableOpacity onPress={onClick}>
      <View
        style={[
          styles.container,
          {
            marginLeft:
              props.shortcut.type === ShortcutType.Wallet
                ? Styles.Spacing.m3
                : Styles.Spacing.m2,
          },
        ]}
      >
        <View style={styles.buttonIconView}>{icon}</View>
        <AVText style={styles.buttonText}>{props.shortcut.title}</AVText>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    height: Styles.Heights.touchTargetHeight2,
    borderRadius: Styles.Heights.touchTargetHeight2 / 2,
    borderWidth: 2,
    borderColor: getDescriber().shortCutButtonBorderColor(),
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Styles.white,
  },
  buttonIconView: {
    marginLeft: Styles.Spacing.m3,
  },
  buttonText: {
    marginLeft: Styles.Spacing.m2,
    marginRight: Styles.Spacing.m3,
    color: Styles.Colors.PayNew.black02,
    fontSize: Styles.Fonts.f8,
    fontWeight: 'bold',
  },
});

export default ShortcutButton;
